import React from 'react';
import { TrendingUp, Plus, Trash2, DollarSign, BarChart2, Briefcase, Building, Calculator, Home, Heart, Award } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Секция управления доходами бюджета
 */
const IncomeSection = ({ 
  formData,
  incomes,
  updateIncomeItem,
  removeIncomeItem,
  addIncomeItem,
  incomeCategories,
  errors
}) => {
  // Функция получения иконки по ID категории
  const getCategoryIcon = (categoryId) => {
    switch (categoryId) {
      case 'salary': return <Briefcase className="category-icon" />;
      case 'business': return <Building className="category-icon" />;
      case 'side_job': return <Calculator className="category-icon" />;
      case 'rental': return <Home className="category-icon" />;
      case 'investments': return <TrendingUp className="category-icon" />;
      case 'pension': return <Heart className="category-icon" />;
      case 'benefits': return <Award className="category-icon" />;
      default: return <Plus className="category-icon" />;
    }
  };

  // Расчет общей суммы доходов
  const totalIncome = incomes.reduce((sum, income) => sum + parseFloat(income.amount || 0), 0);

  // Функция-обработчик изменения полей дохода
  const handleIncomeChange = (id, field, value) => {
    updateIncomeItem(id, field, value);
  };

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-emerald-500" />
          Доходы
        </h3>
        <span className="text-sm text-gray-600 dark:text-gray-400">
          Всего: <span className="font-semibold text-emerald-600 dark:text-emerald-400">{totalIncome.toLocaleString('ru-RU')} ₽</span>
        </span>
      </div>

      {/* Список доходов */}
      {incomes.length === 0 ? (
        <div className="text-center p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
          <p className="text-gray-500 dark:text-gray-400">У вас пока нет добавленных доходов</p>
        </div>
      ) : (
        <div className="space-y-3 mb-4">
          {incomes.map((income, index) => (
            <div 
              key={income.id}
              className="p-3 rounded-lg income-item flex flex-wrap md:flex-nowrap items-start gap-2 group transition-colors"
            >
              {/* Иконка категории */}
              <div className="self-center flex-shrink-0 hidden sm:block">
                {getCategoryIcon(income.category)}
              </div>
              
              {/* Название дохода */}
              <div className="flex-grow min-w-[180px]">
                <label className="text-xs text-gray-600 dark:text-gray-400" htmlFor={`income-name-${income.id}`}>
                  Название
                </label>
                <input
                  type="text"
                  id={`income-name-${income.id}`}
                  value={income.name}
                  onChange={(e) => handleIncomeChange(income.id, 'name', e.target.value)}
                  className={`form-input w-full ${errors[`income_${index}_name`] ? 'input-error' : ''}`}
                />
                {errors[`income_${index}_name`] && (
                  <p className="text-xs text-red-500">{errors[`income_${index}_name`]}</p>
                )}
              </div>
              
              {/* Сумма дохода */}
              <div className="w-full sm:w-32">
                <label className="text-xs text-gray-600 dark:text-gray-400" htmlFor={`income-amount-${income.id}`}>
                  Сумма (₽)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id={`income-amount-${income.id}`}
                    value={income.amount}
                    onChange={(e) => handleIncomeChange(income.id, 'amount', e.target.value)}
                    className={`form-input w-full pr-6 ${errors[`income_${index}_amount`] ? 'input-error' : ''}`}
                  />
                  <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">₽</span>
                </div>
                {errors[`income_${index}_amount`] && (
                  <p className="text-xs text-red-500">{errors[`income_${index}_amount`]}</p>
                )}
              </div>
              
              {/* Категория дохода */}
              <div className="w-full sm:w-40">
                <label className="text-xs text-gray-600 dark:text-gray-400" htmlFor={`income-category-${income.id}`}>
                  Категория
                </label>
                <select
                  id={`income-category-${income.id}`}
                  value={income.category}
                  onChange={(e) => handleIncomeChange(income.id, 'category', e.target.value)}
                  className="form-select w-full"
                >
                  {incomeCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Регулярность дохода */}
              <div className="flex-shrink-0 mt-5 sm:mt-7">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={income.recurring}
                    onChange={(e) => handleIncomeChange(income.id, 'recurring', e.target.checked)}
                    className="form-checkbox"
                  />
                  <span className="ml-2 text-xs text-gray-600 dark:text-gray-400">
                    Регулярный
                  </span>
                </label>
              </div>
              
              {/* Кнопка удаления */}
              <div className="flex-shrink-0 mt-5 sm:mt-6">
                <button
                  type="button"
                  onClick={() => removeIncomeItem(income.id)}
                  className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 p-1 rounded-full transition-colors"
                  aria-label="Удалить доход"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Кнопка добавления дохода */}
      <div className="mt-4">
        <Button
          type="button"
          onClick={addIncomeItem}
          icon={<Plus className="w-4 h-4" />}
          variant="outline"
          size="sm"
        >
          Добавить источник дохода
        </Button>
      </div>

      {/* Полезные советы */}
      <div className="mt-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800">
        <h4 className="text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-2 flex items-center">
          <BarChart2 className="w-4 h-4 mr-1" />
          Полезно знать о доходах
        </h4>
        <ul className="text-xs text-emerald-600 dark:text-emerald-400 space-y-1 list-disc list-inside">
          <li>Средняя зарплата в России в 2025 году составляет около 71 000 ₽</li>
          <li>Для финансовой стабильности рекомендуется иметь несколько источников дохода</li>
          <li>Включите все регулярные доходы (зарплата, подработка, пенсия, социальные выплаты)</li>
          <li>Для нерегулярных доходов (премии, подарки) укажите среднемесячную сумму</li>
        </ul>
      </div>
    </div>
  );
};

export default IncomeSection;